import { showToast } from '@/helper/toast.helper';
import { buildQueryParams } from '@/helper/utils';
import { SiteFormData } from '@/types/project.types';
import {
  AllSitesCountResponseDto,
  AllSitesResponseDto,
  GetSiteByIdResponseDto,
  SiteByProjectIdDto,
  SitesDropdownResponse,
  SitesFilters,
} from '@/types/site.types';
import api from '../instance';

export const sitesService = {
  getSitesList: async (filters?: SitesFilters) => {
    const params = filters ? buildQueryParams(filters) : {};
    try {
      const res = await api.projectApi.get<AllSitesResponseDto>(`/sites`, {
        params,
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });

      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching projects');
    }
  },
  getSiteById: async (id: string) => {
    try {
      const res = await api.projectApi.get<GetSiteByIdResponseDto>(`/sites/${id}`, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching project by ID');
    }
  },

  toggleSiteStatus: async (id: number, status: boolean) => {
    try {
      const res = await api.projectApi.put(
        `/sites/${id}`,
        { status },
        {
          headers: {
            'x-show-loader': 'true',
            'X-Mm-Service': 'mm-content-mgmt-svc',
          },
        },
      );

      return res.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || 'Error updating site status';

      showToast.error(message);
      throw new Error(message);
    }
  },

  getSiteStats: async () => {
    try {
      const res = await api.projectApi.get<AllSitesCountResponseDto>(`/sites/all/count`, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });

      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching projects');
    }
  },
  getSitesByProjectId: async (projectId: number) => {
    try {
      const res = await api.projectApi.get<SiteByProjectIdDto[]>(
        `/sites/project/${projectId}/sites`,
        {
          headers: {
            'x-show-loader': 'true',
            'X-Mm-Service': 'mm-content-mgmt-svc',
          },
        },
      );
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching projects');
    }
  },
  updateSite: async (data: Partial<SiteFormData>) => {
    try {
      const res = await api.projectApi.put<GetSiteByIdResponseDto>(`/sites/${data.id}`, data, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error updating site');
    }
  },
  exportSites: async () => {
    try {
      const res = await api.projectApi.get('/sites/bulk-export', {
        responseType: 'blob',
      });
      return res.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error exporting sites');
    }
  },

  sitesDropdown: async () => {
    try {
      const res = await api.projectApi.get<SitesDropdownResponse>(`/sites/dropdown`, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });

      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching sites');
    }
  },
};
